import type { ReactNode } from 'react';
import { Card } from '@/ui-kit/molecules/card';
import { useCurrentUserContext } from './current-user-context';

/**
 * Client-side route gate on the `pages` reach axis — the page-level sibling of
 * RequireTab.
 *
 * Renders a decision the SERVER already made, delivered as `grants.pages` on
 * /api/me. Nothing is re-derived in the browser.
 *
 * This is an affordance, not the enforcement: the page's routes carry
 * `requirePage`, so a caller who types the URL is refused there regardless of
 * what the SPA renders. Wrapping the route element only means they get a clear
 * panel instead of a broken page.
 *
 * ⚠️ A page gate says nothing about which TABS within it a caller reaches —
 * that is the tabs axis, and a granted page with zero granted tabs is a legal
 * configuration (Q19). Gate both; neither implies the other.
 */
export function RequirePage({ page, children }: { page: string; children: ReactNode }) {
  const { reachesPage } = useCurrentUserContext();
  if (reachesPage(page)) return <>{children}</>;
  return (
    <div className="flex justify-center py-16">
      <Card className="max-w-md text-center">
        <h2 className="text-lg font-semibold text-gray-900">Not available for your role</h2>
        <p className="mt-2 text-sm text-gray-600">
          This page requires the <code className="font-mono">{page}</code> grant.
          Ask an admin to grant a role that includes it.
        </p>
      </Card>
    </div>
  );
}
